import React from 'react';
import hoistStatics from 'hoist-non-react-statics';

/**
 * Create a HOC wrapper around a component that passes in context with
 * an optional mapping from context to props
 *
 * @param {Object} config - configuration for wrapped context HOC
 * @returns {React.Component} wrapped component
 * @example
 * export default createContextConsumerHOC({
 *     component: Card,
 *     context: AccordionContext,
 *     displayName: 'AccordionCardTest',
 *     mapContextToProps: (existingProps, context) => {
 *         const {id} = existingProps;
 *         const {isAccordionOpen, toggleAccordionOpen} = context;
 *         return {
 *             isOpen: isAccordionOpen(id),
 *             onToggleOpen: toggleAccordionOpen
 *         };
 *     }
 * });
 */
export function createContextConsumerHOC(config) {
    const {
        component: ComponentToWrap, // required component to wrap
        context: ContextToConsume, // required context to consume
        mapContextToProps, // optional map from content into props; otherwise entire context passed in as prop
        displayName // optional display name for HOC; defaults to `withContext(component)`
    } = config;

    const wrapper = function (props) {
        return (
            <ContextToConsume.Consumer>
                {
                    (context) => {
                        const contextProps = (mapContextToProps) ? mapContextToProps(props, context) : { context };
                        return <ComponentToWrap {...props} {...contextProps} />;
                    }
                }
            </ContextToConsume.Consumer>
        );
    };

    wrapper.displayName = displayName || `WithContext(${ComponentToWrap.displayName || ComponentToWrap.name})`;
    wrapper.WrappedComponent = ComponentToWrap;

    // hoist these React statics
    if (ComponentToWrap.propTypes) {
        wrapper.propTypes = ComponentToWrap.propTypes;
    }
    if (ComponentToWrap.defaultProps) {
        wrapper.defaultProps = ComponentToWrap.defaultProps;
    }

    // STORY: see if we use 'ref' to the components a lot (vs passing in 'innerRef' prop)
    // and follow the HOC pattern described here: https://reactjs.org/docs/forwarding-refs.html

    // hoist non-React statics
    return hoistStatics(wrapper, ComponentToWrap);
}
